import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useQuery, useQueryClient } from 'react-query'
import { createData, getDataById, getStatus, updateData, getCategories, getRequestType, checkPolicy, checkHistory } from '../core/_requests'
import { Data as Ticket } from '../core/_models'
import { ID, QUERIES } from '../../../../../../../_metronic/helpers'
import { toast } from 'react-toastify'
import { useListView } from '../core/ListViewProvider'
import { formatDateOnly } from '../../../../../../../_metronic/helpers/dateHelpers'

const ticketSchema = Yup.object().shape({
  clientName: Yup.string().required('Client Name is required'),
  phoneNumber: Yup.string().required('Phone Number is required'),
  email: Yup.string().email('Wrong email format').required('Email is required'),
  source: Yup.string().required('Source is required'),
  requestType: Yup.string().required('Request Type is required'),
  category: Yup.string().required('Category is required'),
  title: Yup.string().required('Title is required'),
  detailRequirement: Yup.string().required('Detail Requirement is required'),
  detailSolution: Yup.string().required('Detail Solution is required'),
  status: Yup.string().required('Status is required'),
  noPolicy: Yup.string().required('No Policy is required'),
  durationCreate: Yup.string().nullable(),
  receivedDate: Yup.string().nullable(),
})

type Props = {
  id?: ID
  initialPhoneNumber?: string 
  onSaveSuccess?: (data: Ticket) => void
  onCancel?: () => void
}

export const TicketForm: React.FC<Props> = ({ id, initialPhoneNumber, onSaveSuccess, onCancel }) => {
  const [seconds, setSeconds] = useState(0)
  const { openEditTab } = useListView() as any
  const queryClient = useQueryClient()
  const [isCheckingPolicy, setIsCheckingPolicy] = useState(false)
  const [showPolicyDetail, setShowPolicyDetail] = useState(false) 
  const [historyData, setHistoryData] = useState<any[]>([])
  const [expandedHistory, setExpandedHistory] = useState<number | null>(null);

  const formatDuration = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60
    return [hours, minutes, secs]
      .map((v) => (v < 10 ? '0' + v : v))
      .join(':')
  }

  const loadHistoryOnly = async (policyNo: string) => {
    try {
      const historyRes:any = await checkHistory(policyNo)
      if (historyRes) {
        setHistoryData(historyRes)
      } else {
        setHistoryData([])
      }
    } catch (error) {
      console.error('Failed to load history on init:', error)
    }
  }

 

  const handleCheckPolicy = async () => {
    const policyNo = formik.values.noPolicy;
    if (!policyNo) {
      toast.error('Please enter a policy number first');
      return;
    }

    setIsCheckingPolicy(true);
    try {
      const d: any = await checkPolicy(policyNo);
      const [policyRes, historyRes]: any = await Promise.all([
        checkPolicy(policyNo),
        checkHistory(policyNo)
      ]);
        if (policyRes && policyRes.noPolis){
          
          formik.setFieldValue('agentName', d.namaAgen || '');
          formik.setFieldValue('alamat', `${d.alamatPolis}`);
          formik.setFieldValue('alamat2', `${d.alamatPolis2}`);
          formik.setFieldValue('caraPembayaranPremi', d.spremmeth || '');
          formik.setFieldValue('city', d.kotaPolis || '');
          formik.setFieldValue('clientName', d.namaPemegangPolis || '');
          formik.setFieldValue('coPhoneNumber', d.agentMobilephone || '');
          formik.setFieldValue('commDate', d.commDate || '');
          formik.setFieldValue('distributionChannel', d.distchannel || '');
          formik.setFieldValue('dob', d.bod || '');
          formik.setFieldValue('email', d.semail || '');
          formik.setFieldValue('insured', d.namaTertanggung || '');
          formik.setFieldValue('kodeAgen', d.kodeAgen || '');
          formik.setFieldValue('kodeCabang', d.kodeCabang || '');
          formik.setFieldValue('memberId', d.memberId || '');
          formik.setFieldValue('noPolicy', d.noPolis || '');
          formik.setFieldValue('noSpaj', d.sproposalno || '');
          // formik.setFieldValue('phoneNumber', d.smobiletelno  || '');
          formik.setFieldValue('policyHolder', d.namaPemegangPolis || '');
          formik.setFieldValue('policyStatus', d.statusPolis || '');
          formik.setFieldValue('premi', d.premi || 0);
          formik.setFieldValue('product', d.product || '');
          formik.setFieldValue('registerDate', d.tglissued || '');
          formik.setFieldValue('registerDate', d.tglissued || '');
          formik.setFieldValue('uangPertanggungan', d.uangPertanggungan || 0);
          formik.setFieldValue('provinsi', d.provinsi || '');
          formik.setFieldValue('zipCode', d.kodeposPolis || '');

          setShowPolicyDetail(true); 
        } else {
          toast.warn('Policy not found');
        }
        if (historyRes) {
          setHistoryData(historyRes);
        } else {
          setHistoryData([]);
        }
        toast.success('Policy found and data populated');
      
    } catch (error) {
      toast.error('Failed to check policy');
    } finally {
      setIsCheckingPolicy(false);
    }
  };

  const {data: categories} = useQuery('categories', getCategories, { refetchOnWindowFocus: false })
  const {data: requesttype} = useQuery('requesttype', getRequestType, { refetchOnWindowFocus: false })
  const {data} = useQuery(['ticket-detail', id], () => getDataById(Number(id)), {
    enabled: !!id,
    refetchOnWindowFocus: false,
  })

  const [status, setStatus] = useState<Array<{ id: string; name: string }>>([])

  const formik = useFormik({
    initialValues: {
      id: id ? Number(id) : undefined,
      agentName: '',
      alamat: '',
      alamat2: '',
      category: '',
      caraPembayaranPremi: '',
      city: '',
      clientName: '',
      coPhoneNumber: '',
      commDate: '',
      createdDt: '',
      detailRequirement: '',
      detailSolution: '',
      distributionChannel: '',
      durationCreate: '',
      email: '',
      eTandaTterima: false,
      insured: '',
      isActive: true,
      isAutoClose: false,
      isEmailSent: false,
      isEmailTtd: 0,
      isFromUpload: false,
      isReopen: false,
      rating: 4,
      updatedDt: '',
      version: 1,
      kodeAgen: '',
      kodeCabang: '',
      noPolicy: '',
      noSpaj: '',
      phoneNumber: initialPhoneNumber || '',
      policyHolder: '',
      policyStatus: '',
      premi: '',
      priority: '',
      product: '',
      provinsi: '',
      receivedDate: '',
      registerDate: '',
      requestType: '',
      source: 'Call Center',
      status: '',
      title: '',
      uangPertanggungan: 0,
      zipCode: '',
    },
    validationSchema: ticketSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const payload = { ...values };
      if (payload.receivedDate) payload.receivedDate = new Date(payload.receivedDate).toISOString();

      try {
        if (id) {
          const updatedData = await updateData(payload as Ticket)
          queryClient.invalidateQueries([QUERIES.TICKET_LIST])
          queryClient.invalidateQueries(['ticket-detail', id])
          toast.success(`Ticket Updated`)
          if (onSaveSuccess) onSaveSuccess(updatedData || (payload as Ticket))
        } else {
          const newData = await createData(payload as Ticket)
          queryClient.invalidateQueries([QUERIES.TICKET_LIST])
          if (onSaveSuccess && newData) {
            toast.success(`Ticket Created`)
            onSaveSuccess(newData)
          }
        }
      } catch (error) {
        toast.error(`Failed To Save Ticket`)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const statusResponse = await getStatus()
        setStatus(statusResponse.data)
      } catch (error) {
        console.error('Failed to fetch initial data:', error)
      }
    }
    fetchInitialData()
  }, [])

  useEffect(() => {
    let interval: any
    
    if (!id) {
      interval = setInterval(() => {
        setSeconds((prev) => {
          const nextSec = prev + 1
          formik.setFieldValue('durationCreate', formatDuration(nextSec))
          return nextSec
        })
      }, 1000)
    }

    return () => clearInterval(interval) 
  }, [id])

  useEffect(() => {
    if (data) {
      formik.setValues({
        ...formik.initialValues,
        ...data,
        id: id ? Number(id) : undefined,
        receivedDate: data?.receivedDate ? data.receivedDate.substring(0, 16) : '',
      })
      if (data.noPolicy) {
        loadHistoryOnly(data.noPolicy)
      }

      if (data.product) setShowPolicyDetail(true)
    }
  }, [data, id])

  return (
    <div className='row'>
      <div className='col-lg-8 col-sm-12'>
        <div className='card shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>{id ? 'Edit Ticket' : 'Create New Ticket'}</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>Fill in the ticket information below</span>
            </h3>
          </div>
          <form onSubmit={formik.handleSubmit} className='form'>
            <div className='card-body py-5'>
              
              {/* --- SECTION 1: POLICY DETAILS --- */}
             
              <div className='mb-10'>
                <h5 className='text-primary mb-5'><i className='bi bi-shield-check me-2'></i>Policy & Product</h5>
                
                <div className='row g-9 mb-5'>
                 <div className='col-md-12'>
                  <label className='form-label'>Duration Process</label>
                  <input
                    type='text'
                    className='form-control bg-light-info fw-bold'
                    value={id ? formik.values.durationCreate : formatDuration(seconds)}
                    readOnly
                  />
                </div>
                </div>
                <div className='row g-9 mb-5'>
                  <div className='col-md-4'>
                    <label className='form-label fw-bold'>No. Policy</label>
                    <div className='input-group'>
                      <input type='text' className='form-control form-control-sm' {...formik.getFieldProps('noPolicy')} />
                      <button className='btn btn-primary btn-sm' type='button' disabled={isCheckingPolicy} onClick={handleCheckPolicy}>
                        {isCheckingPolicy ? <span className='spinner-border spinner-border-sm'></span> : 'Check'}
                      </button>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label'>Product Name</label>
                    <input {...formik.getFieldProps('product')} className='form-control form-control-sm'  />
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label'>Policy Status</label>
                    <input {...formik.getFieldProps('policyStatus')} className='form-control form-control-sm'  />
                  </div>
                </div>

                {/* --- COLLAPSIBLE DETAIL BOX --- */}
                {formik.values.product && (
                  <div className='card border border-dashed border-primary bg-light-primary px-5 py-4 mt-5'>
                    <div
                      className='d-flex align-items-center justify-content-between cursor-pointer'
                      onClick={() => setShowPolicyDetail(!showPolicyDetail)}
                    >
                      <div className='d-flex align-items-center'>
                        <i className={`bi bi-chevron-${showPolicyDetail ? 'down' : 'right'} me-3 text-primary`}></i>
                        <span className='fw-bolder text-primary text-uppercase fs-7'>Agent & Policy Details</span>
                      </div>
                      <div className='d-flex gap-2'>
                        <span className='badge badge-primary fs-8'>{formik.values.distributionChannel}</span>
                        <span className={`badge ${formik.values.policyStatus?.toLowerCase() === 'active' ? 'badge-success' : 'badge-danger'} fs-8`}>
                          {formik.values.policyStatus || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {showPolicyDetail && (
                      <div className='mt-5'>
                        <div className='row g-7'>
                          {/* KOLOM KIRI: IDENTitas POLIS */}
                          <div className='col-md-6 border-end border-gray-300'>
                            <div className='mb-4'>
                              <label className='text-muted fs-8 d-block'>Product Name</label>
                              <span className='fw-bold text-gray-800 fs-6'>{formik.values.product}</span>
                            </div>
                            
                            <div className='row mb-4'>
                              <div className='col-6'>
                                <label className='text-muted fs-8 d-block'>Policy Number</label>
                                <span className='fw-bold text-gray-800'>{formik.values.noPolicy || '-'}</span>
                              </div>
                              <div className='col-6'>
                                <label className='text-muted fs-8 d-block'>SPAJ Number</label>
                                <span className='fw-bold text-gray-800'>{formik.values.noSpaj || '-'}</span>
                              </div>
                            </div>

                            <div className='mb-4'>
                              <label className='text-muted fs-8 d-block'>Policy Holder (Pemegang Polis)</label>
                              <span className='fw-bold text-gray-800'>{formik.values.policyHolder || '-'}</span>
                            </div>

                            <div className='mb-4'>
                              <label className='text-muted fs-8 d-block'>Insured (Tertanggung)</label>
                              <span className='fw-bold text-gray-800'>{formik.values.insured || '-'}</span>
                            </div>

                            <div className='row'>
                              <div className='col-6'>
                                <label className='text-muted fs-8 d-block'>Commence Date</label>
                                <span className='fw-bold text-gray-800'>{formik.values.commDate || '-'}</span>
                              </div>
                              <div className='col-6'>
                                <label className='text-muted fs-8 d-block'>Issue Date</label>
                                <span className='fw-bold text-gray-800'>{formik.values.registerDate || '-'}</span>
                              </div>
                            </div>
                          </div>

                          {/* KOLOM KANAN: FINANSIAL & ALAMAT */}
                          <div className='col-md-6'>
                            <div className='mb-4 p-3 bg-white bg-opacity-50 rounded border border-gray-200'>
                              <label className='text-muted fs-8 d-block'>Premium ({formik.values.caraPembayaranPremi || 'Method N/A'})</label>
                              <span className='fw-bolder text-success fs-5'>
                                IDR {Number(formik.values.premi).toLocaleString()}
                              </span>
                              <div className='separator my-2'></div>
                              <label className='text-muted fs-9 text-uppercase'>Sum Assured (UP)</label>
                              <span className='fw-bold text-gray-800 d-block'>
                                IDR {Number(formik.values.uangPertanggungan).toLocaleString()}
                              </span>
                            </div>

                            <div className='mb-0'>
                              <label className='text-muted fs-8 d-block'>Mailing Address</label>
                              <span className='fw-bold text-gray-800 fs-8 d-block'>{formik.values.alamat}</span>
                              {formik.values.alamat2 && formik.values.alamat2 !== 'null' && (
                                <span className='fw-bold text-gray-800 fs-8 d-block'>{formik.values.alamat2}</span>
                              )}
                              <span className='text-gray-600 fs-8 mt-1 d-block italic'>
                                {formik.values.city}, {formik.values.provinsi} {formik.values.zipCode}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* FOOTER BOX: INFO AGENT */}
                        <div className='separator border-primary opacity-25 my-5'></div>
                        
                        <div className='row align-items-center'>
                          <div className='col-md-6'>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-35px symbol-circle me-3'>
                                <span className='symbol-label bg-white text-primary fw-bold'>
                                  {formik.values.agentName?.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <label className='text-muted fs-9 d-block'>Servicing Agent</label>
                                <span className='fw-bold text-gray-800 fs-7'>
                                  {formik.values.agentName} <span className='text-primary fw-normal'>({formik.values.kodeAgen})</span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-6 text-md-end'>
                            <div className='d-inline-block text-start'>
                              <label className='text-muted fs-9 d-block'>Agent Contact & Branch</label>
                              <span className='fw-bold text-gray-800 fs-7'>
                                <i className='bi bi-telephone fill-primary me-1'></i> {formik.values.coPhoneNumber || '-'} 
                                <span className='mx-2 text-gray-400'>|</span>
                                <i className='bi bi-geo-alt me-1'></i> {formik.values.kodeCabang || '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* --- SECTION 2: CUSTOMER INFORMATION --- */}
              <div className='mb-10'>
                <h5 className='text-primary mb-5'><i className='bi bi-person-circle me-2'></i>Customer Information</h5>
                <div className='row g-9 mb-5'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Client Name</label>
                    <input {...formik.getFieldProps('clientName')} className='form-control' disabled={formik.isSubmitting} />
                    {formik.touched.clientName && formik.errors.clientName && <div className='text-danger mt-1'>{formik.errors.clientName}</div>}
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label required'>Phone Number</label>
                    <input {...formik.getFieldProps('phoneNumber')} className='form-control' disabled={formik.isSubmitting} />
                    
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                      <div className='text-danger mt-2'>{String(formik.errors.phoneNumber)}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label required'>Email</label>
                    <input type='email' {...formik.getFieldProps('email')} className='form-control'  disabled={formik.isSubmitting} />
                    
                    {formik.touched.email && formik.errors.email && (
                      <div className='text-danger mt-2'>{String(formik.errors.email)}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* --- SECTION 3: TICKET CLASSIFICATION --- */}
              <div className='mb-10'>
                <h5 className='text-primary mb-5'><i className='bi bi-tags-fill me-2'></i>Ticket Classification</h5>
                <div className='row g-9 mb-5'>
                  <div className='col-md-4'>
                    <label className='form-label required'>Request Type</label>
                    <select {...formik.getFieldProps('requestType')} className='form-select' disabled={formik.isSubmitting}>
                      <option value=''>Select Type</option>
                      {requesttype?.map((t: string) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    
                    {formik.touched.requestType && formik.errors.requestType && (
                      <div className='text-danger mt-2'>{String(formik.errors.requestType)}</div>
                    )}
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label required'>Category</label>
                    <select {...formik.getFieldProps('category')} className='form-select' disabled={formik.isSubmitting}>
                      <option value=''>Select Category</option>
                      {categories?.map((c: string) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {formik.touched.category && formik.errors.category && (
                      <div className='text-danger mt-2'>{String(formik.errors.category)}</div>
                    )}
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label'>Priority</label>
                    <select {...formik.getFieldProps('priority')} className='form-select' disabled={formik.isSubmitting}>
                      <option value=''>Select Priority</option>
                      <option value='Low'>Low</option>
                      <option value='Normal'>Normal</option>
                      <option value='High'>High</option>
                      <option value='Urgent'>Urgent</option>
                    </select>
                    
                    {formik.touched.priority && formik.errors.priority && (
                      <div className='text-danger mt-2'>{String(formik.errors.priority)}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* --- SECTION 4: CASE DETAILS --- */}
              <div className='mb-10'>
                <h5 className='text-primary mb-5'><i className='bi bi-chat-left-text me-2'></i>Case Details</h5>
                <div className='mb-5'>
                  <label className='form-label required'>Title</label>
                  <input {...formik.getFieldProps('title')} className='form-control' placeholder='Brief summary of the issue'  disabled={formik.isSubmitting} />
                  {formik.touched.title && formik.errors.title && (
                    <div className='text-danger mt-2'>{String(formik.errors.title)}</div>
                  )}
                </div>
                <div className='row g-9 mb-5'>
                  <div className='col-md-6'>
                    <label className='form-label required'>Detail Requirement</label>
                    <textarea {...formik.getFieldProps('detailRequirement')} className='form-control' rows={4} disabled={formik.isSubmitting}></textarea>
                    
                    {formik.touched.detailRequirement && formik.errors.detailRequirement && (
                      <div className='text-danger mt-2'>{String(formik.errors.detailRequirement)}</div>
                    )}
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label'>Detail Solution</label>
                    <textarea {...formik.getFieldProps('detailSolution')} className='form-control' rows={4} disabled={formik.isSubmitting}></textarea>
                  </div>
                </div>
              </div>

              <div className='row g-9 mb-8'>
                <div className='col-md-4'>
                    <label className='form-label required'>Status</label>
                    <select {...formik.getFieldProps('status')} className='form-select bg-light-warning'>
                      <option value=''>Select Status</option>
                      {status.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                    </select>
                    {formik.touched.status && formik.errors.status && (
                      <div className='text-danger mt-2'>{String(formik.errors.status)}</div>
                    )}
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label'>Received Date</label>
                    <input type='datetime-local' {...formik.getFieldProps('receivedDate')} className='form-control' disabled={formik.isSubmitting} />
                    
                  </div>
              </div>

            </div>


            <div className='card-footer d-flex justify-content-end py-6 px-9'>
              <button type='button' onClick={onCancel} className='btn btn-sm btn-active-light-primary me-2'>Cancel</button>
              <button type='submit' className='btn btn-sm btn-primary' disabled={formik.isSubmitting}>
                {!formik.isSubmitting ? (id ? 'Update Ticket' : 'Save Ticket') : (
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className='col-lg-4 col-sm-12'>
        <div className='card shadow-sm border-start border-start-3 border-start-info'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Ticket History</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                {historyData.length > 0 ? `${historyData.length} Records Found` : 'No history found'}
              </span>
            </h3>
          </div>
          <div className='card-body pt-2' style={{ maxHeight: '1365px', overflowY: 'auto' }}>
            {historyData.length > 0 ? (
              <div className='accordion accordion-icon-toggle' id='kt_history_accordion'>
                {historyData.map((item, index) => (
                  <div className='mb-5' key={index}>
                    <div
                      className={`accordion-header bg-light-primary py-3 d-flex border border-dashed rounded p-4 cursor-pointer ${
                        expandedHistory === index ? 'bg-light-primary border-primary' : 'bg-light border-gray-300'
                      }`}
                      onClick={() => setExpandedHistory(expandedHistory === index ? null : index)}
                    >
                      <div className='d-flex flex-column flex-grow-1'>
                        <div className='d-flex align-items-center justify-content-between mb-1'>
                          <span className='fs-7 text-muted fw-bold'>
                            {item.registrationNumber ? item.registrationNumber : '-'} 
                          </span>
                          <span className={`badge badge-light-${item.status === 'Open' ? 'warning' : 'success'} fs-9`}>
                            {item.status}
                          </span>
                        </div>
                        
                        <span className='text-gray-800 fw-bolder fs-6'>{item.title || 'Untitled Ticket'}</span>
                        <div className='d-flex gap-3 mt-1'>
                          <span className='text-muted fs-8'>
                            <i className='bi bi-tag fs-9 me-1'></i>{item.category || '-'}
                          </span>
                          <span className='text-muted fs-8'>
                            <i className='bi bi-list fs-9 me-1'></i>{item.requestType || '-'}
                          </span>
                          <span className='text-muted fs-8'>
                            <i className='bi bi-calendar fs-9 me-1'></i>
                            {item.registerdate ? formatDateOnly(new Date(item.registerDate).toLocaleDateString('id-ID')) === 'Invalid Date' ? '-':formatDateOnly(new Date(item.registerDate).toLocaleDateString('id-ID'))  : '-'}
                          </span>
                        </div>
                      </div>
                      <div className='ms-2'>
                        <i className={`bi bi-chevron-${expandedHistory === index ? 'down' : 'right'} text-primary fs-4`}></i>
                      </div>
                    </div>
                    <div className={`collapse ${expandedHistory === index ? 'show' : ''}`}>
                      <div className='p-5 border border-dashed border-top-0 rounded-bottom border-gray-300 bg-white'>
                        <div className='mb-4'>
                          <label className='text-primary fw-bold fs-8 text-uppercase'>Requirement Detail</label>
                          <div className='bg-light-secondary p-3 rounded fs-7 text-gray-700 mt-1' style={{ whiteSpace: 'pre-wrap' }}>
                            {item.detailRequirement || 'No requirement detail provided.'}
                          </div>
                        </div>
                        <div className='mb-0'>
                          <label className='text-success fw-bold fs-8 text-uppercase'>Solution Detail</label>
                          <div className='bg-light-success bg-opacity-10 p-3 rounded fs-7 text-gray-700 mt-1' style={{ whiteSpace: 'pre-wrap' }}>
                            {item.detailSolution || 'Waiting for solution...'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='separator separator-dashed mt-4 mb-2'></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center py-10'>
                <i className='bi bi-clipboard-x fs-1 text-muted'></i>
                <p className='text-muted mt-3'>Please check policy to see history</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
